import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, ChevronRight } from 'lucide-react';
import { getDailyActivity } from '../lib/api';
import { getToken } from '../lib/auth';

const todayKey = () => new Date().toISOString().split('T')[0];

// Short, pleasant two-note chime via Web Audio (no asset needed).
let audioCtx = null;
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    [[880, 0], [1174.66, 0.12]].forEach(([freq, t]) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.16, now + t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.28);
      osc.connect(gain).connect(audioCtx.destination);
      osc.start(now + t);
      osc.stop(now + t + 0.32);
    });
  } catch (e) { /* silent */ }
};
const buzz = () => { try { navigator.vibrate && navigator.vibrate([90, 40, 90]); } catch (e) { /* silent */ } };

// Global in-app toast for new checkouts. Fires on ANY screen while the app is open
// and visible. Complements push notifications (which cover the app-closed case).
export default function NewCheckoutWatcher({ interval = 45000 }) {
  const navigate = useNavigate();
  const seen = useRef(new Set());
  const initialized = useRef(false);
  const day = useRef(todayKey());

  useEffect(() => {
    const card = (onClick, content) => (id) => (
      <div
        role="button"
        onClick={() => { onClick(); toast.dismiss(id); }}
        className="flex items-center gap-3 w-[340px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-lg border border-blue-100 px-4 py-3 cursor-pointer hover:border-blue-300 active:scale-[0.99] transition-all"
        data-testid="new-checkout-toast"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
          <LogOut size={18} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">{content}</div>
        <ChevronRight size={18} className="text-slate-300 flex-shrink-0" />
      </div>
    );

    const showOne = (c) => toast.custom(card(
      () => navigate(`/home?focus=${encodeURIComponent(c.id)}`),
      <>
        <p className="text-sm font-bold text-slate-900 truncate">New checkout · {c.propertyName}</p>
        {c.propertyAddress && <p className="text-xs text-slate-500 truncate">{c.propertyAddress}</p>}
      </>
    ), { duration: 7000 });

    const showSummary = (n) => toast.custom(card(
      () => navigate('/home'),
      <>
        <p className="text-sm font-bold text-slate-900 truncate">{n} new checkouts</p>
        <p className="text-xs text-slate-500 truncate">Tap to see the latest activity</p>
      </>
    ), { duration: 7000 });

    const poll = async () => {
      if (!getToken()) { initialized.current = false; seen.current = new Set(); return; }
      if (document.visibilityState !== 'visible') return;
      const tk = todayKey();
      if (tk !== day.current) { day.current = tk; initialized.current = false; seen.current = new Set(); }
      try {
        const res = await getDailyActivity(tk);
        const checkouts = res.data?.checkouts || [];
        if (!initialized.current) {
          checkouts.forEach((c) => seen.current.add(c.id));
          initialized.current = true;
          return;
        }
        const fresh = checkouts.filter((c) => !seen.current.has(c.id));
        fresh.forEach((c) => seen.current.add(c.id));
        if (fresh.length === 0) return;
        playChime();
        buzz();
        if (fresh.length > 3) showSummary(fresh.length);
        else fresh.forEach(showOne);
      } catch (e) { /* silent */ }
    };

    poll();
    const timer = setInterval(poll, interval);
    const onVis = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', poll);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', poll);
    };
  }, [interval, navigate]);

  return null;
}
